import { usePermissions } from '../../app/hooks/usePermissions';
import { getUserName } from '../../shared/utils/userAdapter';
import { 
  Users, 
  BookOpen, 
  MapPin,
  Award,
  Activity,
  Building2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  adminUserService,
  adminCursoService,
  adminTrilhaService,
  adminEmpresaService
} from '../../shared/services';

export function AdminDashboardPage() {
  const { user, isAdmin, isInstrutor } = usePermissions();

  // Queries individuais (TODO: migrar para /Stats/Dashboard quando backend implementar)
  const { data: usersData } = useQuery({
    queryKey: ['admin-users-count'],
    queryFn: () => adminUserService.getUsers({}),
    enabled: isAdmin,
  });

  const { data: cursosData } = useQuery({
    queryKey: ['admin-cursos-count'],
    queryFn: () => adminCursoService.getCursos({}),
  });

  const { data: trilhasData } = useQuery({
    queryKey: ['admin-trilhas-count'],
    queryFn: () => adminTrilhaService.getTrilhas({}),
    enabled: isAdmin,
  });

  const { data: empresasData } = useQuery({
    queryKey: ['admin-empresas-count'],
    queryFn: () => adminEmpresaService.getEmpresas({}),
    enabled: isAdmin,
  });

  const isLoading = false; // Dados carregam individualmente

  const adminCards = [
    {
      title: 'Usuários',
      value: (usersData as any[])?.length || 0,
      icon: Users,
      color: 'blue',
      link: '/admin/usuarios',
      show: isAdmin,
    },
    {
      title: 'Cursos',
      value: (cursosData as any[])?.length || 0,
      icon: BookOpen,
      color: 'green',
      link: '/admin/cursos',
      show: true,
    },
    {
      title: 'Trilhas',
      value: (trilhasData as any[])?.length || 0,
      icon: MapPin,
      color: 'purple',
      link: '/admin/trilhas',
      show: isAdmin,
    },
    {
      title: 'Empresas',
      value: (empresasData as any[])?.length || 0,
      icon: Building2,
      color: 'orange',
      link: '/admin/empresas',
      show: isAdmin,
    },
  ];

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Activity className="w-8 h-8 text-blue-600" />
            Painel Administrativo
          </h1>
          <p className="text-gray-600 mt-2">
            Bem-vindo, {getUserName(user)}! 
            {isAdmin ? ' Você tem acesso completo ao sistema.' : ' Você pode gerenciar cursos.'}
          </p>
        </div>

        {/* Cards de Estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {isLoading ? (
            // Loading state
            <div className="col-span-3 bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3 animate-spin" />
              <p className="text-gray-600">Carregando estatísticas...</p>
            </div>
          ) : (
            adminCards.filter(card => card.show).map((card) => {
              const Icon = card.icon;
              const colorClasses = {
                blue: 'bg-blue-100 text-blue-600',
                green: 'bg-green-100 text-green-600',
                purple: 'bg-purple-100 text-purple-600',
                orange: 'bg-orange-100 text-orange-600',
                red: 'bg-red-100 text-red-600',
              }[card.color];

              return (
                <Link
                  key={card.title}
                  to={card.link}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{card.title}</p>
                      <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${colorClasses}`}>
                      <Icon className="w-8 h-8" />
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>

        {/* Ações Rápidas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {isAdmin && (
              <Link
                to="/admin/usuarios/novo"
                className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Users className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-900">Novo Usuário</p>
                  <p className="text-sm text-gray-600">Cadastrar novo usuário</p>
                </div>
              </Link>
            )}
            
            <Link
              to="/admin/cursos/novo"
              className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <BookOpen className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-gray-900">Novo Curso</p>
                <p className="text-sm text-gray-600">Criar novo curso</p>
              </div>
            </Link>

            {isAdmin && (
              <Link
                to="/admin/trilhas/nova"
                className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <MapPin className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-gray-900">Nova Trilha</p>
                  <p className="text-sm text-gray-600">Criar trilha de aprendizado</p>
                </div>
              </Link>
            )}
          </div>
        </div>

        {/* Informações de Permissão */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <Award className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">Suas Permissões</h3>
              <p className="text-sm text-blue-700 mb-2">
                Você está logado como <strong>{user?.roles?.join(', ') || 'Usuário'}</strong>
              </p>
              <ul className="text-sm text-blue-700 space-y-1">
                {isAdmin && (
                  <>
                    <li>• Gerenciar usuários e permissões</li>
                    <li>• Criar e editar trilhas de aprendizado</li>
                  </>
                )}
                {(isAdmin || isInstrutor) && (
                  <li>• Criar e gerenciar cursos</li>
                )}
                <li>• Acessar relatórios e estatísticas</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
